import { Injectable } from '@angular/core';

import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { AuthService } from '../auth/auth.service';

import { LogoutDialogComponent } from '@components/messages/logout-dialog/logout-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class SessionExpireService {
  updateTimeRemaining$ = new BehaviorSubject<any>(null);
  isModalVisible$ = new BehaviorSubject<boolean>(false);
  subscription: Subscription;

  private timeOut: ReturnType<typeof setInterval> | null = null;
  private modalTimeOut: any;
  private isModalVisible: boolean = false;
  private isLogoutClicked: boolean = false;
  private interval: any;
  private readonly CHECK_INTERVAL = 100; // Check every 2 seconds
  private readonly MODAL_OFFSET = 300000; // Show modal 5 minutes before expiration
  private readonly SESSION_DURATION = 1200000; // 20 minutes in milliseconds

  get expirationTime(): any {
    return Number(localStorage.getItem('expirationTime')) || 0;
  }

  constructor(
    private authService: AuthService,
    private dialog: MatDialog
  ) {
    // Trigger when the user returns to the browser tab
    document.addEventListener('visibilitychange', this.handleAndUpdateSessionExpiration.bind(this));
    this.subscription = new Subscription();
  }

  /**
   * Set session expiration time and manage modal for user confirmation
   *
   * Using performance.now() instead of Date.now() because it provides higher precision
   * and is not affected by changes in the system clock (e.g., time zone changes).
   * performance.now() returns time relative to the page load, ensuring more accurate time measurements.
   */
  setExpirationTime() {
    const expirationTime = performance.now() + this.SESSION_DURATION;

    // Save time in localStorage
    localStorage.setItem('expirationTime', expirationTime.toString());

    // Clear previous intervals and timeouts
    if (this.interval) {
      clearInterval(this.interval);
    }

    if (this.modalTimeOut) {
      clearTimeout(this.modalTimeOut);
    }

    this.setTimeOutToShowModal();
    this.setTimeOutToCheckExpiration();
  }

  checkIsLogoutClicked(): boolean {
    return this.isLogoutClicked;
  }

  /**
   * Returns the remaining seconds
   *
   * Using performance.now() instead of Date.now() because it provides higher precision
   * and is not affected by changes in the system clock (e.g., time zone changes).
   * performance.now() returns time relative to the page load, ensuring more accurate time measurements.
   */
  getRemainingTime(): number {
    return Math.max(0, Math.floor((this.expirationTime - performance.now()) / 1000));
  }

  /**
   * Get the default session expiration time (20 minutes)
   */
  getExpirationTime(): number {
    return this.SESSION_DURATION;
  }

  /**
   * Update remaining time for countdown (e.g., for showing timer in the modal)
   */
  updateExpirationTime(seconds: number): any {
    return timer(0, 1000).pipe(
      map(n => (seconds - n) * 1000),
      takeWhile(n => n >= 0)
    );
  }

  /**
   * Set timeout to show modal (e.g., 5 minutes)
   */
  private setTimeOutToShowModal(): void {
    const modalTime = this.SESSION_DURATION - this.MODAL_OFFSET;
    if (modalTime > 0) {
      this.modalTimeOut = setInterval(() => {
        if (!this.isModalVisible) {
          this.showLogoutModal();
        }
      }, modalTime);
    }
  }

  /**
   * Set timeout to check expiration time
   */
  private setTimeOutToCheckExpiration(): void {
    this.interval = setInterval(() => {
      this.handleSessionExpiration();
    }, this.CHECK_INTERVAL);
  }

  private handleSessionExpiration() {
    if (this.expirationTime && this.getRemainingTime() <= 0) {
      clearInterval(this.interval);
      this.authService.logout();
    }
  }

  private handleAndUpdateSessionExpiration() {
    if (this.expirationTime && this.getRemainingTime() <= 0) {
      clearInterval(this.interval);
      this.authService.logout();
    } else {
      this.updateTimeRemaining$.next(this.getRemainingTime());
    }
  }

  /**
   * Show a modal to the user to confirm if they want to stay logged in or logout
   */
  private showLogoutModal() {
    const dialogRef: MatDialogRef<LogoutDialogComponent> = this.dialog.open(LogoutDialogComponent, {
      disableClose: true,
      autoFocus: true,
      hasBackdrop: true,
      data: {
        title: 'Your session is about to expire',
        message: 'Do you want to stay logged in?',
        confirmText: 'Stay',
        cancelText: 'Log out',
      },
    });

    this.isModalVisible = true;
    this.isModalVisible$.next(this.isModalVisible);

    this.subscription.add(
      dialogRef.afterClosed().subscribe(data => {
        if (data?.action === 'cancel') {
          if (data.result) {
            this.resetExpirationTime(); // The user chooses to stay logged in
          } else {
            this.isLogoutClicked = true;
            this.authService.logout(); // The user chooses to log out
          }
          this.isModalVisible = false;
          this.isModalVisible$.next(this.isModalVisible);
        }
      })
    );
  }

  /**
   * Reset session expiration time when user confirms to stay logged in
   */
  private resetExpirationTime() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    if (this.modalTimeOut) {
      clearTimeout(this.modalTimeOut);
    }
    this.setExpirationTime(); // Resets the timers
  }
}
